/// <amd-module name="Agile/Sprints/Fabric/Components/EditIterationDialog" />
import { Dialog, IDialogProps } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { ITextFieldProps, TextField } from "VSSUI/TextField";
import { DateTimePicker } from "VSSUI/DateTimePicker";
import { datePickerStrings } from "VSSUI/Components/DateTimePicker/Helpers";
import * as Resources from "Agile/Sprints/Fabric/Resources";
import { VssComponent } from "VSS/Platform/Layout";
import * as React from "react";
import { IIterationData } from "Agile/Contracts/Common";

interface IEditIterationDialogState {
    iterationName: string;
    iterationStartDate: Date | undefined;
    iterationEndDate: Date | undefined;
    errorMessage?: string;
}

interface IEditIterationDialogProps {
    selectedIteration: IIterationData;
    onDismiss: () => void;
}

export class EditIterationDialog extends React.Component<IEditIterationDialogProps, IEditIterationDialogState> {
    constructor(props: IEditIterationDialogProps) {
        super(props);
        this.state = {
            iterationName: props.selectedIteration.name,
            iterationStartDate: props.selectedIteration.startDate,
            iterationEndDate: props.selectedIteration.finishDate,
            errorMessage: undefined
        };
    }

    public render(): JSX.Element {
        const { onDismiss } = this.props;
        const dialogProps: IDialogProps = {
            titleProps: { text: Resources.editIterationTitle, className: "edit-iteration-dialog-title" },
            onDismiss: onDismiss,
            lightDismiss: false,
            showCloseButton: true,
            footerButtonProps: [
                {
                    text: Resources.saveAndClose,
                    disabled: this.state.errorMessage !== "",
                    primary: true,
                    onClick: onDismiss
                },
                {
                    text: Resources.cancel,
                    onClick: onDismiss
                }
            ]
        };

        return (
            <Dialog {...dialogProps}>
                {this.state.errorMessage && this._renderErrorMessage()}
                <div className="edit-iteration-dialog-header">{Resources.iterationName}</div>
                {this._renderIterationName()}
                {this._renderDate(Resources.startDate, this.state.iterationStartDate)}
                {this._renderDate(Resources.endDate, this.state.iterationEndDate)}
                {this._renderLocation()}
            </Dialog>
        );
    }

    private _renderIterationName(): JSX.Element {
        const props: ITextFieldProps = {
            value: this.state.iterationName,
            onChange: this._onNameChange
        };

        return (
            <FormItem label={Resources.iterationName}>
                <TextField {...props} />
            </FormItem>
        );
    }

    private _renderErrorMessage(): JSX.Element {
        return <div className="edit-iteration-dialog-error">{this.state.errorMessage}</div>;
    }

    private _renderDate(label: string, date?: Date): JSX.Element {
        return (
            <DateTimePicker
                label={label}
                allowTextInput={true}
                formatDate={this._formatDate}
                onSelectDate={label === Resources.startDate ? this._startDateChanged : this._endDateChanged}
                parseDateFromString={this._parseDate}
                value={date}
                strings={datePickerStrings()}
                dateOnly={true}
                disabled={false}
            />
        );
    }

    private _renderLocation(): JSX.Element {
        const friendlyPath = this.props.selectedIteration.friendlyPath;
        const endPosition = friendlyPath.lastIndexOf("\\");
        const iterationPath = friendlyPath.substring(0, endPosition);

        const props: ITextFieldProps = {
            value: iterationPath,
            disabled: true
        };

        return (
            <FormItem label={Resources.location}>
                <TextField {...props} />
            </FormItem>
        );
    }

    private _formatDate = (date: Date): string => {
        if (!date) {
            return "";
        }

        return date.toLocaleDateString(navigator.language);
    };

    private _parseDate = (value: string): Date => {
        // TODO: Need date parser with locale #1447923
        return new Date(Date.parse(value));
    };

    private _onNameChange = (event, value: string) => {
        var patt = /^[^\\\/$?*:"&<>#%|\+]*$/; //invalid iteration characters regular expression literal
        if (!patt.test(value)) {
            this.setState({ errorMessage: Resources.editDialogInvalidNameTitle });
        } else if (value === "") {
            this.setState({ errorMessage: Resources.editIterationNameRequired });
        } else {
            this.setState({ errorMessage: "" });
        }
        this.setState({ iterationName: value });
    };

    private _endDateChanged = (date: Date): void => {
        if (date) {
            // DST bug: Fabric DatePicker can return dates with nonzero hours over DST boundaries.
            // Zero out the date hours
            date.setHours(0);
        }

        if (this.state.iterationStartDate) {
            this._validateDate(this.state.iterationStartDate, date);
        }

        this.setState({ iterationEndDate: date });
    };

    private _startDateChanged = (date: Date): void => {
        if (date) {
            // DST bug: Fabric DatePicker can return dates with nonzero hours over DST boundaries.
            // Zero out the date hours
            date.setHours(0);
        }

        if (this.state.iterationEndDate) {
            this._validateDate(date, this.state.iterationEndDate);
        }

        this.setState({ iterationStartDate: date });
    };

    private _validateDate = (startDate: Date, endDate: Date): void => {
        if (startDate > endDate) {
            this.setState({ errorMessage: Resources.startDateAfterFinish });
        } else {
            this.setState({ errorMessage: "" });
        }
    };
}

VssComponent.register("editIterationDialog", EditIterationDialog);
